import { Group, Progress, Stack, Text } from "@mantine/core";
import type { AssistantUsageText } from "@/locales/assistant-usage";
import type { UsageMeterDto } from "@/types/ai-assistant-usage";
import {
	formatDuration,
	formatUsd,
	LEVEL_COLOR,
	meterAmount,
	meterTitle,
} from "./cap.logic";

interface Props {
	meter: UsageMeterDto;
	t: AssistantUsageText;
	lang: "id" | "en";
	resetInSeconds: number;
}

/** Satu batang meter (Chat/Suara × hari/bulan): terpakai vs batas, estimasi dolar, jumlah permintaan. */
export function CapMeterBar({ meter, t, lang, resetInSeconds }: Props) {
	const used = meterAmount(meter, meter.used, lang);
	const unit = meter.unit === "seconds" ? "" : " token";
	const summary =
		meter.limit > 0
			? t.usedOfLimit(
					`${used}${unit}`,
					`${meterAmount(meter, meter.limit, lang)}${unit}`,
				)
			: `${t.usedOnly(`${used}${unit}`)} · ${t.unlimited}`;
	const reset = formatDuration(resetInSeconds, lang);
	const usd =
		meter.usd === null
			? t.usdNone
			: `${t.usdEstimate(formatUsd(meter.usd, lang))}${meter.usdIncomplete ? ` — ${t.usdIncomplete}` : ""}`;

	return (
		<Stack gap={6} data-usage-meter={meter.id} data-level={meter.level}>
			<Group justify="space-between" gap="xs">
				<Text fw={600} size="sm">
					{meterTitle(meter, t)}
				</Text>
				{meter.percent !== null && (
					<Text size="sm" fw={600} c={LEVEL_COLOR[meter.level]}>
						{meter.percent}%
					</Text>
				)}
			</Group>
			<Progress
				value={Math.min(meter.percent ?? 0, 100)}
				color={LEVEL_COLOR[meter.level]}
				size="md"
				radius="xl"
				aria-label={meterTitle(meter, t)}
			/>
			<Text size="sm">{summary}</Text>
			<Text size="xs" c="dimmed">
				{usd}
			</Text>
			<Text size="xs" c="dimmed">
				{t.requests(meter.requests)} ·{" "}
				{meter.period === "day" ? t.resetDay(reset) : t.resetMonth(reset)}
			</Text>
			{meter.noUsageRequests > 0 && (
				<Text size="xs" c="orange">
					{t.noUsageNote(meter.noUsageRequests)}
				</Text>
			)}
		</Stack>
	);
}
